import { useEffect, useState } from "react";
import { CalendarClock, Check, Plus, Trash2, X } from "lucide-react";
import api from "../lib/api";
import { money, monthLabel, todayISO } from "../lib/format";
import { notify } from "../lib/toast";

function UpcomingModal({ open, onClose, onSave, contacts, accounts }) {
  const [form, setForm] = useState({
    kind: "Tahsilat",
    tone: "income",
    title: "",
    amount: "",
    date: todayISO(),
    contact_id: "",
    account_id: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        kind: "Tahsilat",
        tone: "income",
        title: "",
        amount: "",
        date: todayISO(),
        contact_id: "",
        account_id: "",
        notes: "",
      });
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!form.title.trim()) return setError("Başlık gerekli");
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return setError("Geçerli bir tutar girin");
    setBusy(true);
    try {
      await onSave({ ...form, amount: amt });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="upcoming-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">YENİ PLAN</span>
            <h2>Yaklaşan hareket ekle</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="close-upcoming-modal">
            <X size={18} />
          </button>
        </div>
        <div className="segmented">
          <button
            className={form.tone === "income" ? "active" : ""}
            onClick={() => setForm({ ...form, tone: "income", kind: "Tahsilat" })}
            data-testid="upcoming-type-income"
          >
            Tahsilat
          </button>
          <button
            className={form.tone === "expense" ? "active" : ""}
            onClick={() => setForm({ ...form, tone: "expense", kind: "Ödeme" })}
            data-testid="upcoming-type-expense"
          >
            Ödeme
          </button>
        </div>
        <label>
          Başlık
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Örn. Ay sonu maaş ödemesi"
            data-testid="upcoming-title-input"
          />
        </label>
        <div className="form-grid">
          <label>
            Tutar (₺)
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="upcoming-amount-input"
            />
          </label>
          <label>
            Tarih
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="upcoming-date-input"
            />
          </label>
        </div>
        <div className="form-grid">
          <label>
            Cari
            <select
              value={form.contact_id}
              onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
              data-testid="upcoming-contact-select"
            >
              <option value="">Seçilmedi</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label>
            Hesap
            <select
              value={form.account_id}
              onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              data-testid="upcoming-account-select"
            >
              <option value="">Seçilmedi</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.bank} · {a.last_four}</option>
              ))}
            </select>
          </label>
        </div>
        {error ? <div className="form-error">{error}</div> : null}
        <button className="primary-btn full" onClick={submit} disabled={busy} data-testid="save-upcoming-button">
          <Plus size={17} /> {busy ? "Kaydediliyor…" : "Planı kaydet"}
        </button>
      </div>
    </div>
  );
}

export default function UpcomingPage() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [u, c, a] = await Promise.all([
        api.get("/upcoming"),
        api.get("/contacts"),
        api.get("/accounts"),
      ]);
      setItems(u.data);
      setContacts(c.data);
      setAccounts(a.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const complete = async (u) => {
    try {
      await api.post(`/upcoming/${u.id}/complete`);
      notify.success(`${u.tone === "income" ? "Tahsilat" : "Ödeme"} tamamlandı`);
      load();
    } catch {
      notify.error("İşlem tamamlanamadı");
    }
  };
  const remove = async (u) => {
    if (!window.confirm(`"${u.title}" silinsin mi?`)) return;
    try {
      await api.delete(`/upcoming/${u.id}`);
      notify.success("Kayıt silindi");
      load();
    } catch {
      notify.error("Silme başarısız");
    }
  };

  const planned = items.filter((i) => i.status === "planned");
  const incomeTotal = planned.filter((i) => i.tone === "income").reduce((s, i) => s + i.amount, 0);
  const expenseTotal = planned.filter((i) => i.tone === "expense").reduce((s, i) => s + i.amount, 0);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">PLANLAMA</div>
          <h1>Ödemeler & Tahsilatlar</h1>
          <p>Yaklaşan tüm nakit hareketlerinizi kronolojik olarak izleyin.</p>
        </div>
        <div className="head-actions">
          <button className="primary-btn" onClick={() => setModal(true)} data-testid="add-upcoming-button">
            <Plus size={17} /> Plan ekle
          </button>
        </div>
      </div>

      <div className="account-summary panel">
        <div>
          <span>Bekleyen tahsilat</span>
          <strong className="income-text">{money(incomeTotal)}</strong>
        </div>
        <div>
          <span>Bekleyen ödeme</span>
          <strong className="expense-text">{money(expenseTotal)}</strong>
        </div>
        <div>
          <span>Net beklenen</span>
          <strong>{money(incomeTotal - expenseTotal)}</strong>
        </div>
      </div>

      <div className="panel inner-page" data-testid="upcoming-panel">
        {loading ? (
          <div className="page-loading">Yükleniyor…</div>
        ) : items.length === 0 ? (
          <div className="empty-state" data-testid="upcoming-empty">
            <CalendarClock size={30} />
            <h3>Planlı hareket yok</h3>
            <p>İlk tahsilat ya da ödeme planınızı ekleyerek başlayın.</p>
            <button className="primary-btn" onClick={() => setModal(true)} data-testid="empty-add-upcoming-btn">
              <Plus size={14} /> Plan ekle
            </button>
          </div>
        ) : (
          <div className="upcoming-timeline">
            {items.map((u) => (
              <div className={`timeline-row ${u.status}`} key={u.id} data-testid={`upcoming-row-${u.id}`}>
                <div className={`date-box ${u.tone}`}>
                  <strong>{(u.date || "").slice(8, 10)}</strong>
                  <small>{monthLabel((u.date || "").slice(0, 7))}</small>
                </div>
                <div className="tl-main">
                  <strong>{u.title}</strong>
                  <span>{u.kind}{u.status === "completed" ? " · Tamamlandı" : ""}</span>
                </div>
                <div className={u.tone === "income" ? "income-text" : "expense-text"}>
                  {u.tone === "income" ? "+" : "−"}
                  {money(u.amount)}
                </div>
                {u.status === "planned" ? (
                  <button
                    className="icon-btn ghost"
                    onClick={() => complete(u)}
                    data-testid={`complete-upcoming-${u.id}`}
                    aria-label="Tamamla"
                  >
                    <Check size={14} />
                  </button>
                ) : null}
                <button
                  className="icon-btn ghost"
                  onClick={() => remove(u)}
                  data-testid={`delete-upcoming-${u.id}`}
                  aria-label="Sil"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <UpcomingModal
        open={modal}
        onClose={() => setModal(false)}
        onSave={async (payload) => {
          await api.post("/upcoming", payload);
          notify.success("Plan eklendi");
          await load();
        }}
        contacts={contacts}
        accounts={accounts}
      />
    </>
  );
}
